<script>
import { useBanobagiAvailability } from "@/composables/useBanobagiAvailability";
import { isBanobagiConnectionClosed } from "@/utils/banobagiAvailability";
import { isBanobagiCampaignChannel } from "@/utils/banobagiRights";

export default {
  name: "BanobagiMarketplacePanel",
  props: {
    channels: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: ""
    }
  },
  emits: ["select-channel", "retry"],
  setup() {
    return useBanobagiAvailability();
  },
  computed: {
    // แคมเปญนี้นับสิทธิ์จาก Lazada เท่านั้น ถ้ามีหลายร้านให้แสดงร้านที่เชื่อมแล้วก่อน
    lazadaChannel() {
      const lazadaChannels = this.channels.filter(
        channel => channel.id != 0 && isBanobagiCampaignChannel(channel)
      );
      return (
        lazadaChannels.find(channel => channel.isConnected) ||
        lazadaChannels[0] ||
        null
      );
    },
    isConnected() {
      return Boolean(this.lazadaChannel?.isConnected);
    },
    channelImage() {
      const channel = this.lazadaChannel;
      return (
        channel?.marketplaceUrl || channel?.image_url || channel?.logo || ""
      );
    }
  },
  methods: {
    selectChannel() {
      this.refreshAvailability();
      if (isBanobagiConnectionClosed()) return;
      this.$emit("select-channel", this.lazadaChannel || {});
    }
  }
};
</script>

<template>
  <section class="banobagi-marketplace" data-test="profile-marketplace-panel">
    <div
      v-if="isLoading"
      class="banobagi-marketplace__loading"
      data-test="profile-marketplace-loading"
      role="status"
      :aria-label="$t('loading')"
    ></div>

    <div
      v-else-if="errorMessage"
      class="banobagi-marketplace__error"
      data-test="profile-marketplace-error-msg"
      role="alert"
    >
      <span>{{ errorMessage }}</span>
      <button
        type="button"
        class="banobagi-marketplace__retry"
        data-test="profile-marketplace-retry-btn"
        @click="$emit('retry')"
      >
        {{ $t("retry") }}
      </button>
    </div>

    <button
      v-else
      type="button"
      class="banobagi-marketplace__channel"
      data-test="profile-marketplace-lazada-btn"
      :disabled="isConnectionClosed"
      @click="selectChannel"
    >
      <span class="banobagi-marketplace__icon-shell">
        <img
          v-if="channelImage"
          :src="channelImage"
          alt=""
          class="banobagi-marketplace__logo"
        />
        <font-awesome-icon v-else :icon="['fas', 'store-alt']" />
      </span>

      <span class="banobagi-marketplace__text">
        <span class="banobagi-marketplace__title">
          {{
            isConnected
              ? $t("banobagi_lazada_connected_title") ||
                "บัญชี Lazada เชื่อมต่อแล้ว"
              : $t("banobagi_lazada_connect_title") || ""
          }}
        </span>
        <span class="banobagi-marketplace__description">
          {{
            isConnected
              ? $t("banobagi_lazada_connected_desc") ||
                "คำสั่งซื้อใหม่นับสิทธิ์ให้อัตโนมัติ"
              : $t("banobagi_lazada_connect_desc") || ""
          }}
        </span>
      </span>

      <span
        :class="[
          'banobagi-marketplace__status',
          { 'is-connected': isConnected }
        ]"
        data-test="profile-marketplace-lazada-status"
      >
        {{ isConnected ? $t("btn_connect_success_market") : $t("btn_connect") }}
      </span>
    </button>
  </section>
</template>

<style lang="scss" scoped>
// ขอบบนซ่อนอยู่ใต้การ์ดสิทธิ์ จึงเผื่อ padding บนเท่ากับระยะที่ซ้อนกัน
.banobagi-marketplace {
  --banobagi-marketplace-overlap: 24px;

  position: relative;
  margin: calc(var(--banobagi-marketplace-overlap) * -1) 16px 0;
  padding: calc(var(--banobagi-marketplace-overlap) + 8px) 12px 8px;
  border-radius: 0 0 24px 24px;
  background: #1352c7;
}

.banobagi-marketplace__channel {
  display: flex;
  width: 100%;
  min-height: 44px;
  align-items: center;
  gap: 8px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #ffffff;
  cursor: pointer;
  font: inherit;
  text-align: left;
}

.banobagi-marketplace__channel:focus-visible {
  border-radius: 16px;
  outline: 2px solid #ffffff;
  outline-offset: 2px;
}

.banobagi-marketplace__channel:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.banobagi-marketplace__icon-shell {
  display: inline-flex;
  width: 36px;
  height: 36px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 50%;
  background: #ffffff;
  color: #aaa3b2;
  font-size: var(--font-body-lg);
}

.banobagi-marketplace__logo {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.banobagi-marketplace__text {
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  flex-direction: column;
}

.banobagi-marketplace__title {
  font-size: var(--font-body);
  font-weight: 600;
  line-height: 1.3;
}

.banobagi-marketplace__description {
  font-size: var(--font-helper);
  line-height: 1.3;
}

.banobagi-marketplace__status {
  flex: 0 0 auto;
  padding: 8px 12px;
  border-radius: 12px;
  background: var(--primary-color);
  color: #ffffff;
  font-size: var(--font-body-sm);
  font-weight: 500;
}

.banobagi-marketplace__status.is-connected {
  border: 1px solid #52c183;
  background: #17698a;
  color: #c9f7dc;
}

.banobagi-marketplace__error {
  display: flex;
  min-height: 44px;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #ffffff;
  text-align: center;
}

.banobagi-marketplace__retry {
  min-width: 88px;
  min-height: 44px;
  padding: 8px 16px;
  border: 1px solid #ffffff;
  border-radius: var(--crm-border-radius);
  background: transparent;
  color: #ffffff;
  cursor: pointer;
  font: inherit;
  font-weight: 600;
}

.banobagi-marketplace__retry:focus-visible {
  outline: 2px solid #ffffff;
  outline-offset: 2px;
}

.banobagi-marketplace__loading {
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(
    110deg,
    rgba(255, 255, 255, 0.3) 30%,
    rgba(255, 255, 255, 0.7) 50%,
    rgba(255, 255, 255, 0.3) 70%
  );
  background-size: 200% 100%;
  animation: banobagi-marketplace-shimmer 1.2s linear infinite;
}

@keyframes banobagi-marketplace-shimmer {
  to {
    background-position-x: -200%;
  }
}
</style>
